import blindCast from "@connectedliving/common/lib/utilities/lang/blindCast";
import {
  IonItem,
  IonLabel,
  IonNote,
  IonSelect,
  IonSelectOption,
  isPlatform,
} from "@ionic/react";
import React, { PropsWithChildren, ReactElement, useCallback } from "react";
import { FieldInputProps } from "src/state/formState/useFieldInputs";
import { IonicChangeHandler } from "src/utilities/ionic/IonicChangeHandler";
import FormFieldErrors from "./FormFieldErrors";

export type FormSelectOption<ValueType> = {
  value: ValueType;
  label: string;
};

export type FormSelectProps<ValueType> = Omit<
  React.ComponentProps<typeof IonSelect>,
  "onIonChange" | "onIonBlur" | "onChange" | "onBlur"
> &
  FieldInputProps<ValueType> & {
    label: string;
    labelPosition?: "fixed" | "floating" | "stacked";
    helperText?: string;
    buttonLabels: { ok: string; cancel: string };
    options: readonly FormSelectOption<ValueType>[];
  };

// eslint-disable-next-line react/function-component-definition
function FormSingleSelect<OptionValues>({
  onChange,
  onBlur,
  options,
  errors,
  label: selectLabel,
  labelPosition,
  helperText,
  buttonLabels,
  ...restProps
}: PropsWithChildren<FormSelectProps<OptionValues>>): ReactElement | null {
  const onIonChange: IonicChangeHandler = useCallback(
    (event) => {
      onChange(
        blindCast<
          OptionValues,
          "we will only render options with values from the `options` prop so we can assume the value received here comes from one of them"
        >(event.detail.value),
      );
      onBlur();
    },
    [onBlur, onChange],
  );

  return (
    <>
      <IonItem lines="inset">
        <IonLabel
          position={
            labelPosition || (isPlatform("ios") ? "stacked" : "floating")
          }
        >
          {selectLabel}
        </IonLabel>
        <IonSelect
          {...restProps}
          {...{ onIonChange }}
          onIonBlur={onBlur}
          okText={buttonLabels.ok}
          cancelText={buttonLabels.cancel}
        >
          {options.map(({ label, value }) => (
            <IonSelectOption value={value} key={String(value)}>
              {label}
            </IonSelectOption>
          ))}
        </IonSelect>
        {helperText && <IonNote slot="helper">{helperText}</IonNote>}
      </IonItem>
      <FormFieldErrors {...{ errors }} />
    </>
  );
}

export default FormSingleSelect;
